// ============================================================
// システムデータ整合 — 決定的クエリエンジン（2026-07-28）
//
// 設計の芯: **AI は「条件」しか作らない。実データの抽出・件数・値はここが決める。**
//   agent-rules.md #1「もっともらしく作って答えない」を、プロンプトの言い聞かせではなく
//   構造で担保するための層。Gemini が返した JSON は必ず parseQuerySpec / parseEditOp を
//   通し、ホワイトリスト（fields.ts）に無いフィールド・未知の演算子は**黙って捨てる**。
//
// このファイルは純粋関数のみ（fetch も DOM も触らない）。regression テストは
// src/pages/reconcile/__tests__/queryEngine.test.ts。
// ============================================================

import { FieldDef, FieldKind, getField, fieldLabel } from './fields';

export type FilterOp = 'is_empty' | 'is_not_empty' | 'eq' | 'neq' | 'gt' | 'lt' | 'contains';

const VALID_OPS: FilterOp[] = ['is_empty', 'is_not_empty', 'eq', 'neq', 'gt', 'lt', 'contains'];

/** 値を必要としない演算子 */
const UNARY_OPS: FilterOp[] = ['is_empty', 'is_not_empty'];

/** 数値でしか意味を持たない演算子 */
const NUMERIC_ONLY_OPS: FilterOp[] = ['gt', 'lt'];

export interface Filter {
  field: string;
  op: FilterOp;
  value?: string | number;
}

export interface QuerySpec {
  dateField: 'start_date' | 'end_date';
  dateFrom: string | null;
  dateTo: string | null;
  filters: Filter[];
}

export type EditOp =
  | { type: 'set'; field: string; value: string; onlyWhenEmpty: boolean }
  | { type: 'copy'; from: string; field: string; onlyWhenEmpty: boolean };

// ============================================================
// 値の読み取り
// ============================================================

/**
 * 予約から対象フィールドの現在値を読む。
 * mapReservation が detail 値を root に持ち上げているケースがあるため、
 * コードベースの慣習どおり root → detail の順にフォールバックする
 * （例: ReservationList.tsx の `res.contact_media ?? res.detail?.contact_media`）。
 */
export function readField(reservation: any, key: string): any {
  if (!reservation || typeof reservation !== 'object') return undefined;
  const rootValue = reservation[key];
  if (rootValue !== undefined && rootValue !== null && rootValue !== '') return rootValue;
  const detailValue = reservation.detail?.[key];
  if (detailValue !== undefined) return detailValue;
  return rootValue;
}

/** 「未入力」の判定。数値の 0 とフラグの 0 も「未入力」として扱う（現場の語感に合わせる） */
export function isEmptyValue(value: any, kind: FieldKind): boolean {
  if (value === null || value === undefined) return true;
  if (kind === 'string') return String(value).trim() === '';
  if (kind === 'number') {
    if (String(value).trim() === '') return true;
    const n = Number(value);
    return !Number.isFinite(n) || n === 0;
  }
  // flag
  return !(value === true || value === 1 || value === '1');
}

/** 表示用に整形した現在値 */
export function formatValue(value: any, kind: FieldKind): string {
  if (isEmptyValue(value, kind)) return kind === 'flag' ? 'なし' : '（未入力）';
  if (kind === 'flag') return 'あり';
  if (kind === 'number') return Number(value).toLocaleString('ja-JP');
  return String(value);
}

// ============================================================
// AI 出力の検証（ホワイトリスト方式）
// ============================================================

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function sanitizeDate(value: any): string | null {
  return typeof value === 'string' && DATE_RE.test(value.trim()) ? value.trim() : null;
}

function parseFilter(raw: any): Filter | null {
  if (!raw || typeof raw !== 'object') return null;
  const def = getField(raw.field);
  if (!def) return null; // ホワイトリスト外 → 捨てる
  const op = raw.op;
  if (!VALID_OPS.includes(op)) return null;

  if (UNARY_OPS.includes(op)) return { field: def.key, op };

  if (raw.value === undefined || raw.value === null || String(raw.value).trim() === '') return null;

  if (NUMERIC_ONLY_OPS.includes(op)) {
    const n = Number(raw.value);
    if (!Number.isFinite(n)) return null;
    return { field: def.key, op, value: n };
  }
  if (op === 'contains' && def.kind !== 'string') return null;

  if (def.kind === 'number' || def.kind === 'flag') {
    const n = Number(raw.value);
    if (Number.isFinite(n)) return { field: def.key, op, value: n };
  }
  return { field: def.key, op, value: String(raw.value) };
}

/** Gemini が返した query を検証して QuerySpec にする。使える条件が1つも無ければ null */
export function parseQuerySpec(raw: any): QuerySpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const filters = Array.isArray(raw.filters)
    ? (raw.filters.map(parseFilter).filter(Boolean) as Filter[])
    : [];
  const dateFrom = sanitizeDate(raw.date_from);
  const dateTo = sanitizeDate(raw.date_to);
  if (filters.length === 0 && !dateFrom && !dateTo) return null;
  return {
    dateField: raw.date_field === 'end_date' ? 'end_date' : 'start_date',
    dateFrom,
    dateTo,
    filters,
  };
}

/**
 * Gemini が返した edit を検証して EditOp にする。
 * **書き込み先が editable でなければ null**（＝価格系は構造的に書き換え不能）。
 */
export function parseEditOp(raw: any): EditOp | null {
  if (!raw || typeof raw !== 'object') return null;
  const target = getField(raw.field ?? raw.to);
  if (!target || !target.editable) return null;
  const onlyWhenEmpty = raw.only_when_empty !== false; // 既定は安全側（空欄のときだけ書く）

  if (raw.type === 'copy') {
    const from = getField(raw.from);
    if (!from || from.key === target.key) return null;
    return { type: 'copy', from: from.key, field: target.key, onlyWhenEmpty };
  }
  if (raw.type === 'set') {
    if (raw.value === undefined || raw.value === null) return null;
    return { type: 'set', field: target.key, value: String(raw.value), onlyWhenEmpty };
  }
  return null;
}

// ============================================================
// 抽出（決定的）
// ============================================================

function toDateKey(value: any): string {
  const s = String(value ?? '');
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dd}`;
}

export function matchesFilter(reservation: any, filter: Filter, def: FieldDef): boolean {
  const value = readField(reservation, filter.field);
  switch (filter.op) {
    case 'is_empty':
      return isEmptyValue(value, def.kind);
    case 'is_not_empty':
      return !isEmptyValue(value, def.kind);
    case 'gt':
    case 'lt': {
      const n = Number(value);
      const target = Number(filter.value);
      if (!Number.isFinite(n) || !Number.isFinite(target)) return false;
      return filter.op === 'gt' ? n > target : n < target;
    }
    case 'contains': {
      if (isEmptyValue(value, def.kind)) return false;
      return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
    }
    case 'eq':
    case 'neq': {
      let hit: boolean;
      if (def.kind === 'string') {
        hit = String(value ?? '').trim().toLowerCase() === String(filter.value).trim().toLowerCase();
      } else {
        hit = Number(value) === Number(filter.value);
      }
      return filter.op === 'eq' ? hit : !hit;
    }
    default:
      return false;
  }
}

/** クエリ仕様を予約配列に適用する。ここが件数の唯一の出どころ */
export function filterReservations(reservations: any[], spec: QuerySpec): any[] {
  const defs = spec.filters
    .map((f) => ({ filter: f, def: getField(f.field) }))
    .filter((x): x is { filter: Filter; def: FieldDef } => x.def !== null);

  return (reservations || []).filter((r) => {
    if (spec.dateFrom || spec.dateTo) {
      const key = toDateKey(r?.[spec.dateField]);
      if (!key) return false;
      if (spec.dateFrom && key < spec.dateFrom) return false;
      if (spec.dateTo && key > spec.dateTo) return false;
    }
    return defs.every(({ filter, def }) => matchesFilter(r, filter, def));
  });
}

const OP_LABEL: Record<FilterOp, string> = {
  is_empty: 'が未入力',
  is_not_empty: 'が入力済み',
  eq: 'が',
  neq: 'が以外',
  gt: 'が超',
  lt: 'が未満',
  contains: 'に含む',
};

/** 抽出条件を人が読める短い文言（チップ表示用）にする */
export function describeQuery(spec: QuerySpec): string[] {
  const chips: string[] = [];
  const dateLabel = spec.dateField === 'end_date' ? '返却日' : '貸出日';
  if (spec.dateFrom && spec.dateTo) chips.push(`${dateLabel} ${spec.dateFrom} 〜 ${spec.dateTo}`);
  else if (spec.dateFrom) chips.push(`${dateLabel} ${spec.dateFrom} 以降`);
  else if (spec.dateTo) chips.push(`${dateLabel} ${spec.dateTo} 以前`);

  spec.filters.forEach((f) => {
    const label = fieldLabel(f.field);
    if (UNARY_OPS.includes(f.op)) chips.push(`${label}${OP_LABEL[f.op]}`);
    else chips.push(`${label}${OP_LABEL[f.op]} ${f.value}`);
  });
  return chips;
}

// ============================================================
// 一括編集の差分プレビュー（ドライラン）
// ============================================================

export interface EditPlanRow {
  id: string | number;
  contactCode: string;
  customerName: string;
  startDate: string;
  /** 変更前の生値 */
  before: any;
  /** 変更後の生値 */
  after: any;
  /** 値が実際に変わるか。false の行は実行対象から外す */
  willChange: boolean;
  /** 変更しない理由（onlyWhenEmpty で弾かれた等） */
  skipReason?: string;
}

/**
 * 選択された予約に編集操作を当てたらどうなるかを計算する（**保存はしない**）。
 * 実行対象は willChange === true の行だけ。
 */
export function buildEditPlan(reservations: any[], edit: EditOp): EditPlanRow[] {
  const target = getField(edit.field);
  if (!target || !target.editable) return [];
  const source = edit.type === 'copy' ? getField(edit.from) : null;
  if (edit.type === 'copy' && !source) return [];

  return (reservations || []).map((r) => {
    const before = readField(r, target.key);
    const beforeEmpty = isEmptyValue(before, target.kind);

    let after: any;
    let skipReason: string | undefined;

    if (edit.type === 'set') {
      after = edit.value;
    } else {
      const src = readField(r, source!.key);
      if (isEmptyValue(src, source!.kind)) {
        after = before;
        skipReason = `${source!.label}が未入力`;
      } else {
        after = target.kind === 'number' ? Number(src) : String(src);
      }
    }

    if (!skipReason && edit.onlyWhenEmpty && !beforeEmpty) {
      after = before;
      skipReason = `${target.label}が入力済み`;
    }

    const willChange = !skipReason && String(after ?? '') !== String(before ?? '');
    if (!willChange && !skipReason) skipReason = '変更なし';

    return {
      id: r?.id,
      contactCode: String(r?.contact_code || r?.detail?.contact_id || ''),
      customerName: String(r?.customer_name || ''),
      startDate: toDateKey(r?.start_date),
      before,
      after,
      willChange,
      skipReason,
    };
  });
}

/** 編集操作を人が読める文言にする */
export function describeEdit(edit: EditOp): string {
  const target = fieldLabel(edit.field);
  const scope = edit.onlyWhenEmpty ? '（未入力のときだけ）' : '（常に上書き）';
  if (edit.type === 'set') return `${target} に「${edit.value}」を設定${scope}`;
  return `${fieldLabel(edit.from)} を ${target} に複製${scope}`;
}
